<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_DATA_HANDLING_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              Excel
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="dataHandlingRef"
      :items="dataHandlingReport"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? toDateWithYear(data.value) : '' }}
        </p>
      </template>
      <template #cell(databaseSizeInGB)="data">
        <p class="mb-0">
          {{ data.value ? `${data.value} GB` : '' }}
        </p>
      </template>
      <template #cell(databaseSizeInMB)="data">
        <p class="mb-0">
          {{ data.value ? `${data.value} MB` : '' }}
        </p>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="dataHandlingRules">
        <b-form>
          <b-row class="match-height">
            <!-- Financial Date From -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.startDate"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Financial Date From -->
            <!-- Financial Date To -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.endDate"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- Financial Date To -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import Button from '../../components/button/Button.vue'
import DatePickerField from '../../forms/DatePickerField.vue'
import toNumber from '../../../helpers/toNumber'
import {
  toDateWithYear, toYearWithDate, toDateWithCustomTime, toCustomDate,
} from '@/helpers/formatDate'
import { adminPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      toFormatAmount,
      formatAmount,
      toDateWithYear,
      toDateWithCustomTime,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Database Size Tracker'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'databaseSizeInGB', sortable: true, label: 'Database Size (GB)' },
        { key: 'databaseSizeInMB', sortable: true, label: 'Database Size (MB)' },
      ],

      filter: {
        startDate: null,
        endDate: null,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
      dataHandlingReport: [],
      allSearchedData: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Financial Date': this.formatCustomDate(item.financialDate),
        'Database Size': `${item.databaseSizeInGB} GB`,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchDataHandlingReports()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchDataHandlingReports()
    },
  },
  mounted() {
    this.fetchDataHandlingReports()
  },
  methods: {
    ...mapActions({
      fetchDataHandlingReport: 'admin/fetchDataHandlingReport',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchDataHandlingReports()
    },

    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.dataHandlingRules.validate().then(async success => {
        if (success) {
          this.fetchDataHandlingReports()
        }
      })
    },
    async fetchDataHandlingReports() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const filteredData = {
          ...this.filter,
          startDate: this.filter.startDate !== undefined || null ? toYearWithDate(this.filter.startDate) : null,
          endDate: this.filter.endDate !== undefined || null ? toYearWithDate(this.filter.endDate) : null,
        }
        const response = await this.fetchDataHandlingReport({
          search: filteredData,
          page: this.currentPage,
          limit: this.perPage,
          permission: adminPermissions.DATA_HANDLING_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.dataHandlingReport = data.data
          this.totalCount = data.totalItems
        } else {
          this.dataHandlingReport = []
          this.totalCount = 0
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Database Size Tracker failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.fetchDataHandlingReport({
          search: this.filter,
          page: 1,
          limit: 1000000,
          permission: adminPermissions.EXPORT_DATA_HANDLING_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Database Size Tracker'],
            [`${(this.filter.startDate !== null && this.filter.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.filter.startDate)} - ${this.formatCustomDate(this.filter.endDate)}` : 'Date Range: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Database Size Tracker.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.fetchDataHandlingReport({
          search: this.filter,
          page: 1,
          limit: 1000000,
          permission: adminPermissions.EXPORT_DATA_HANDLING_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Database Size Tracker.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]
          const Heading = [
            [orgName],
            ['Database Size Tracker'],
            [`${(this.filter.startDate !== null && this.filter.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.filter.startDate)} - ${this.formatCustomDate(this.filter.endDate)}` : 'Date Range: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = this.allSearchedData.map((item, index) => ({
            'S/N': index + 1,
            'Financial Date': this.formatCustomDate(item.financialDate),
            'Database Size (GB)': `${item.databaseSizeInGB} GB`,
            'Database Size (MB)': `${item.databaseSizeInMB} MB`,
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Database Size Tracker')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
